/**
 * Filter Bar
 *
 * Two modes, chosen per instance via `data-mode`:
 *
 *   alm    — proxy custom buttons + search to a hidden ALM Filters instance.
 *            Used by archives that still rely on Ajax Load More (e.g. /news/).
 *
 *   client — filter a fully-rendered list in the DOM directly. Supports
 *            multi-taxonomy pills, fuzzy search (Fuse) with relevance
 *            ranking when a query is present, <mark> highlighting, and
 *            URL-param-driven initial state.
 *
 * Popover open/close/focus-trap/escape behavior is handled by the
 * global popover module.
 */

import Fuse from "fuse.js";
import { initPopover } from "./popover.js";

const ACTIVE_CLASS = "active";
const HIDDEN_CLASS = "is-hidden";
const SEARCH_MODE_CLASS = "is-search-mode";
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function initFilterBars() {
  document.querySelectorAll(".filter-bar").forEach((bar) => initFilterBar(bar));
}

function initFilterBar(bar) {
  const mode = bar.dataset.mode === "client" ? "client" : "alm";

  // ---- Shared popover setup ------------------------------------------------

  const popoverEl = bar.querySelector(".popover");
  const toggle = bar.querySelector(".filter-bar-toggle");
  let popoverCtrl = null;

  if (toggle && popoverEl) {
    popoverCtrl = initPopover({
      popover: popoverEl,
      toggle,
      onOpen: () => positionPopover(popoverEl, toggle),
    });
  }

  if (mode === "client") {
    initClientMode(bar, popoverEl, popoverCtrl);
  } else {
    initAlmMode(bar, popoverEl, popoverCtrl);
  }
}

// ---------------------------------------------------------------------------
// ALM mode (unchanged behavior — proxies to hidden ALM filter links)
// ---------------------------------------------------------------------------

function initAlmMode(bar, popoverEl, popoverCtrl) {
  const almContainer = bar.querySelector(".filter-bar-alm");
  const searchInput = bar.querySelector(".filter-bar-search-input");

  // Remove hidden ALM elements from tab order (aria-hidden alone doesn't
  // prevent Tab focus). We can't use `inert` because we need programmatic
  // clicks to still work for the proxy mechanism.
  if (almContainer) {
    almContainer.querySelectorAll(FOCUSABLE).forEach((el) => {
      el.setAttribute("tabindex", "-1");
    });
  }

  function handleFilterClick(e) {
    const btn = e.target.closest(
      ".filter-bar-btn:not(.filter-bar-toggle), .filter-bar-category-btn",
    );
    if (!btn) return;

    // Same double-bubble guard as client mode — popoverEl is inside bar on
    // desktop, so without stopPropagation this handler fires twice per click.
    e.stopPropagation();

    const termSlug = btn.dataset.term;
    if (!termSlug) return;

    const taxonomy = btn.dataset.taxonomy || bar.dataset.taxonomy;

    const almLink = findAlmLink(almContainer, termSlug, taxonomy);
    if (almLink) {
      almLink.click();
    }

    if (popoverCtrl && popoverEl && !popoverEl.hidden) {
      popoverCtrl.close(true);
    }

    setTimeout(() => syncAlmActiveStates(bar, almContainer), 50);
  }

  bar.addEventListener("click", handleFilterClick);
  // Popover can be moved to <body> in modal mode, which detaches its
  // click events from the bar. Attach the handler directly so filter
  // clicks inside the popover still fire in that state.
  if (popoverEl) popoverEl.addEventListener("click", handleFilterClick);

  if (searchInput && almContainer) {
    const almSearch = almContainer.querySelector(".alm-filter--search input");
    if (almSearch) {
      const fireAlmSearch = () => {
        almSearch.value = searchInput.value;
        almSearch.dispatchEvent(
          new KeyboardEvent("keyup", {
            key: "Enter",
            keyCode: 13,
            which: 13,
            bubbles: true,
          }),
        );
      };

      let debounceId = null;
      searchInput.addEventListener("input", () => {
        clearTimeout(debounceId);
        debounceId = setTimeout(fireAlmSearch, 300);
      });

      searchInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          clearTimeout(debounceId);
          fireAlmSearch();
        }
      });
    }
  }

  if (almContainer) {
    const observer = new MutationObserver(() => {
      syncAlmActiveStates(bar, almContainer);
      mobile?.refresh();
    });
    observer.observe(almContainer, {
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    syncAlmActiveStates(bar, almContainer);
  }

  // Server-driven preselection from path URLs like /news/category/{slug}/.
  // ALM renders the filtered first batch via shortcode attrs; we just need
  // the matching pill to show active state on init.
  const preTax = bar.dataset.preselectedTax;
  const preSlug = bar.dataset.preselectedSlug;
  if (preTax && preSlug) {
    const btn = bar.querySelector(
      `.filter-bar-btn[data-term="${CSS.escape(preSlug)}"], .filter-bar-category-btn[data-term="${CSS.escape(preSlug)}"]`,
    );
    if (btn) {
      btn.classList.add(ACTIVE_CLASS);
      btn.setAttribute("aria-pressed", "true");
    }
  }

  const mobile = setupMobileFilterPopover(bar, null, { bar });
  mobile?.refresh();
}

function findAlmLink(almContainer, termSlug, taxonomy) {
  if (!almContainer) return null;

  const links = almContainer.querySelectorAll(".alm-filter--link");
  for (const link of links) {
    const linkValue = link.dataset.value || "";
    if (linkValue === termSlug) {
      if (taxonomy) {
        const filterGroup = link.closest(".alm-filter");
        const groupTaxonomy = filterGroup ? filterGroup.dataset.taxonomy : "";
        if (groupTaxonomy && groupTaxonomy !== taxonomy) continue;
      }
      return link;
    }
  }

  return null;
}

function syncAlmActiveStates(bar, almContainer) {
  if (!almContainer) return;

  const activeLinks = almContainer.querySelectorAll(".alm-filter--link.active");
  const activeSlugs = new Set();
  activeLinks.forEach((link) => {
    if (link.dataset.value) activeSlugs.add(link.dataset.value);
  });

  const customBtns = bar.querySelectorAll(
    ".filter-bar-btn[data-term], .filter-bar-category-btn[data-term]",
  );
  customBtns.forEach((btn) => {
    const isActive = activeSlugs.has(btn.dataset.term);
    btn.classList.toggle(ACTIVE_CLASS, isActive);
    btn.setAttribute("aria-pressed", String(isActive));
  });
}

// ---------------------------------------------------------------------------
// Client mode (no server round-trip — filters DOM directly)
// ---------------------------------------------------------------------------

function initClientMode(bar, popoverEl, popoverCtrl) {
  const targetSel = bar.dataset.target;
  if (!targetSel) return;

  const list = document.querySelector(targetSel);
  if (!list) return;

  // Drop a shadow under the inline (desktop) filter-bar once it pins.
  trackStuck(bar, getHeaderHeight());

  const ul = list.querySelector(".list-items");
  const items = Array.from(list.querySelectorAll(".list-item"));
  if (!items.length) return;

  const section = list.classList.contains("content-list")
    ? list
    : list.closest(".content-list");

  const searchInput = bar.querySelector(".filter-bar-search-input");
  const alphaNav = document.querySelector(".has-alpha-nav-layout > .alpha-nav");
  const mobileAlphaNav = section
    ? section.querySelector(".alpha-nav-mobile")
    : null;

  // Cache original innerHTML for headline + description per item so we can
  // restore cleanly before re-applying <mark> highlights on each search update.
  const originals = new Map();
  items.forEach((item) => {
    const h = item.querySelector(".list-item-headline");
    const d = item.querySelector(".list-item-description");
    originals.set(item, {
      headlineEl: h,
      headlineHtml: h ? h.innerHTML : null,
      descriptionEl: d,
      descriptionHtml: d ? d.innerHTML : null,
    });
  });

  // Two separate Fuse instances rather than one with weighted keys: Fuse
  // averages match scores across keys, so a keyword-only match (title score
  // 1.0, keyword score 0.0) lands above the threshold and gets rejected.
  // Searching each field independently lets a perfect keyword hit pass on
  // its own merit; we then offset the keyword-instance scores so title
  // matches still rank ahead in the final order.
  const searchData = items.map((item) => ({
    el: item,
    title: item.querySelector(".list-item-headline")?.textContent || "",
    keywords: item.dataset.keywords || "",
    description:
      item.querySelector(".list-item-description")?.textContent || "",
  }));

  const fuseOpts = {
    threshold: 0.35,
    ignoreLocation: true,
    includeScore: true,
  };
  const titleFuse = new Fuse(searchData, { keys: ["title"], ...fuseOpts });
  const keywordsFuse = new Fuse(searchData, {
    keys: ["keywords"],
    ...fuseOpts,
  });

  // No-results message. Prefer a server-rendered element (so it can
  // include the shared empty-state illustration); fall back to a plain
  // paragraph for callers that don't render one.
  let noResults = list.querySelector(".content-list-no-results");
  if (!noResults) {
    noResults = document.createElement("p");
    noResults.className = "content-list-no-results";
    noResults.textContent = "No tools match the selected filters.";
    noResults.setAttribute("role", "status");
    list.appendChild(noResults);
  }
  noResults.hidden = true;

  // State.
  const state = {
    selected: new Map(), // taxonomy → Set<slug>
    search: "",
  };

  // Initialize selected taxonomy state from URL params so linking
  // in via e.g. `?_research-tool-subjects=reference` preselects pills.
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.forEach((value, key) => {
    const tax = key.replace(/^_/, "");
    if (!tax) return;
    value.split(",").forEach((slug) => {
      const trimmed = slug.trim();
      if (!trimmed) return;
      if (!state.selected.has(tax)) state.selected.set(tax, new Set());
      state.selected.get(tax).add(trimmed);
    });
  });

  // Server-driven preselection from path URLs like /research-tools/{subject}/
  // and /research-tools/available-in-{lang}/. These are separate from URL
  // query params so the canonical path URL stays clean.
  const preTax = bar.dataset.preselectedTax;
  const preSlug = bar.dataset.preselectedSlug;
  if (preTax && preSlug) {
    if (!state.selected.has(preTax)) state.selected.set(preTax, new Set());
    state.selected.get(preTax).add(preSlug);
  }

  // Pill clicks.
  function handleFilterClick(e) {
    const btn = e.target.closest(
      ".filter-bar-btn:not(.filter-bar-toggle), .filter-bar-category-btn",
    );
    if (!btn) return;

    // Popover lives inside .filter-bar on desktop, so a button click bubbles
    // through popoverEl's listener AND bar's listener — without this the
    // same click would toggle the selection twice (net zero).
    e.stopPropagation();

    const slug = btn.dataset.term;
    if (!slug) return;

    const taxRaw = btn.dataset.taxonomy || bar.dataset.taxonomy || "";
    const tax = taxRaw.replace(/^_/, "");

    if (!state.selected.has(tax)) state.selected.set(tax, new Set());
    const set = state.selected.get(tax);
    if (set.has(slug)) set.delete(slug);
    else set.add(slug);
    if (set.size === 0) state.selected.delete(tax);

    if (popoverCtrl && popoverEl && !popoverEl.hidden) {
      popoverCtrl.close(true);
    }

    applyFilters();
  }

  bar.addEventListener("click", handleFilterClick);
  // Popover can be moved to <body> in modal mode, which detaches its
  // click events from the bar. Attach the handler directly so filter
  // clicks inside the popover still fire in that state.
  if (popoverEl) popoverEl.addEventListener("click", handleFilterClick);

  // Search input — debounced; also commit immediately on Enter.
  if (searchInput) {
    let debounceId = null;
    const commit = () => {
      state.search = searchInput.value.trim();
      applyFilters();
    };

    searchInput.addEventListener("input", () => {
      clearTimeout(debounceId);
      debounceId = setTimeout(commit, 150);
    });
    searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        clearTimeout(debounceId);
        commit();

        // If we're inside the mobile filter popover, hitting Enter should
        // dismiss it so the user can see the results.
        const popoverEl = searchInput.closest(".popover");
        if (popoverEl && popoverEl.__popoverInstance) {
          popoverEl.__popoverInstance.close();
        }
      }
    });

    const form = searchInput.closest("form");
    if (form) form.addEventListener("submit", (e) => e.preventDefault());
  }

  const mobile = setupMobileFilterPopover(bar, section, {
    state,
    searchInput,
    onClearSearch: () => {
      state.search = "";
      if (searchInput) searchInput.value = "";
      applyFilters();
    },
  });

  applyFilters();

  function applyFilters() {
    const hasSearch = state.search.length > 0;
    const scoreMap = hasSearch
      ? buildScoreMap(titleFuse, keywordsFuse, searchData, state.search)
      : null;

    // Compute visibility + collect visible items for ordering.
    const visible = [];
    items.forEach((item) => {
      const passesTax = itemPassesTaxonomyFilter(item, state.selected);
      const passesSearch = !hasSearch || scoreMap.has(item);
      const pass = passesTax && passesSearch;
      item.classList.toggle(HIDDEN_CLASS, !pass);
      if (pass) visible.push(item);
    });

    // Reorder the <ul>: relevance sort when searching, otherwise original A-Z.
    if (ul) {
      if (hasSearch) {
        visible.sort((a, b) => (scoreMap.get(a) ?? 1) - (scoreMap.get(b) ?? 1));
        visible.forEach((el) => ul.appendChild(el));
      } else {
        items.forEach((el) => ul.appendChild(el));
      }
    }

    // Reset + re-apply highlights.
    items.forEach((item) => {
      const o = originals.get(item);
      if (o.headlineEl && o.headlineHtml !== null) {
        o.headlineEl.innerHTML = o.headlineHtml;
      }
      if (o.descriptionEl && o.descriptionHtml !== null) {
        o.descriptionEl.innerHTML = o.descriptionHtml;
      }
      if (hasSearch && !item.classList.contains(HIDDEN_CLASS)) {
        if (o.headlineEl) highlightIn(o.headlineEl, state.search);
        if (o.descriptionEl) highlightIn(o.descriptionEl, state.search);
      }
    });

    updatePillStates(bar, state.selected);

    noResults.hidden = visible.length > 0;

    // Alpha-nav: slide off when searching (relevance order nullifies A-Z).
    // For the mobile select we use a class instead of `hidden` so we don't
    // fight content-list.js's breakpoint-driven visibility on resize.
    if (alphaNav) alphaNav.classList.toggle(SEARCH_MODE_CLASS, hasSearch);
    if (mobileAlphaNav) {
      mobileAlphaNav.classList.toggle(SEARCH_MODE_CLASS, hasSearch);
    }

    // Ask the alpha-nav to refresh which letters are reachable.
    if (section) {
      section.dispatchEvent(
        new CustomEvent("content-list:items-changed", { bubbles: true }),
      );
    }

    // Refresh the mobile sticky bar's filter-count badge + search chip.
    mobile?.refresh();
  }
}

function itemPassesTaxonomyFilter(item, selected) {
  for (const [tax, slugs] of selected) {
    const raw = item.getAttribute(`data-tax-${tax}`) || "";
    const itemSlugs = raw.split(/\s+/).filter(Boolean);
    // OR within a taxonomy, AND across taxonomies.
    if (!itemSlugs.some((s) => slugs.has(s))) {
      return false;
    }
  }
  return true;
}

function buildScoreMap(titleFuse, keywordsFuse, searchData, query) {
  const map = new Map();
  const q = query.toLowerCase();

  // Pass 1 — exact substring matches in any field always outrank fuzzy hits.
  // A fuzzy title match like "Reports" for "Sports" should never beat a real
  // keyword/description hit on "sports" elsewhere.
  searchData.forEach((d) => {
    if (d.title.toLowerCase().includes(q)) {
      map.set(d.el, 0.0);
    } else if (d.keywords.toLowerCase().includes(q)) {
      map.set(d.el, 0.2);
    } else if (d.description.toLowerCase().includes(q)) {
      map.set(d.el, 0.4);
    }
  });

  // Very short queries: skip Fuse (bitap penalty is too harsh) but still try
  // an "every char present" pass for prefix-style typing like "fe" → matching
  // "Fletcher" via f+e. Mirrors the /branches/ archive.
  if (query.length <= 2) {
    const allCharsPresent = (str, chars) => {
      const lower = str.toLowerCase();
      return [...chars].every((c) => lower.includes(c));
    };
    searchData.forEach((d) => {
      if (map.has(d.el)) return;
      if (allCharsPresent(d.title, q)) map.set(d.el, 0.6);
      else if (allCharsPresent(d.keywords, q)) map.set(d.el, 0.7);
    });
    return map;
  }

  // Pass 2 — Fuse fuzzy fallback for items not already matched. Title hits
  // outrank keyword hits within the fuzzy tier.
  titleFuse.search(query).forEach((r) => {
    if (map.has(r.item.el)) return;
    map.set(r.item.el, 0.6 + (r.score ?? 0));
  });
  keywordsFuse.search(query).forEach((r) => {
    if (map.has(r.item.el)) return;
    map.set(r.item.el, 0.7 + (r.score ?? 0));
  });

  return map;
}

function updatePillStates(bar, selected) {
  const btns = bar.querySelectorAll(
    ".filter-bar-btn[data-term], .filter-bar-category-btn[data-term]",
  );
  btns.forEach((btn) => {
    const slug = btn.dataset.term;
    const taxRaw = btn.dataset.taxonomy || bar.dataset.taxonomy || "";
    const tax = taxRaw.replace(/^_/, "");
    const set = selected.get(tax);
    const active = !!(set && set.has(slug));
    btn.classList.toggle(ACTIVE_CLASS, active);
    btn.setAttribute("aria-pressed", String(active));
  });
}

/**
 * Wrap substring matches inside an element with <mark>, preserving inline
 * markup (anchors, emphasis, etc.). Walks text nodes only.
 */
function highlightIn(el, query) {
  if (!el || !query) return;
  const pattern = new RegExp(escapeRegex(query), "gi");
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((node) => {
    const text = node.nodeValue;
    if (!text) return;
    const matches = [...text.matchAll(pattern)];
    if (!matches.length) return;

    const frag = document.createDocumentFragment();
    let lastIdx = 0;
    matches.forEach((m) => {
      if (m.index > lastIdx) {
        frag.appendChild(document.createTextNode(text.slice(lastIdx, m.index)));
      }
      const mark = document.createElement("mark");
      mark.textContent = m[0];
      frag.appendChild(mark);
      lastIdx = m.index + m[0].length;
    });
    if (lastIdx < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIdx)));
    }
    node.parentNode.replaceChild(frag, node);
  });
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// ---------------------------------------------------------------------------
// Sticky-shadow helper
//
// Toggles .is-stuck on a sticky element by comparing its current bounding
// rect top to the header-height offset on scroll. rAF-throttled so we never
// run more than once per frame. Returns a teardown function for callers
// that build their sticky element dynamically (e.g. the mobile-filter-bar).
// ---------------------------------------------------------------------------

function getHeaderHeight() {
  return (
    parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--header-height",
      ),
      10,
    ) || 0
  );
}

function trackStuck(el, topOffset) {
  if (!el) return () => {};

  let ticking = false;
  const check = () => {
    ticking = false;
    el.classList.toggle(
      "is-stuck",
      el.getBoundingClientRect().top <= topOffset,
    );
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(check);
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  check();

  return () => {
    window.removeEventListener("scroll", onScroll);
    el.classList.remove("is-stuck");
  };
}

// ---------------------------------------------------------------------------
// Mobile filter popover
//
// On viewports ≤ 850px the inline filter-bar moves into a JS-built popover,
// triggered from a funnel-icon button that sits next to the alpha-nav
// "jump to letter" select inside a single sticky bar. On desktop the
// filter-bar lives inline as a sticky element and none of this runs.
//
// We tear the structure down on resize past the breakpoint so each mode
// gets its own clean DOM.
// ---------------------------------------------------------------------------

const MOBILE_BREAKPOINT_QUERY = "(max-width: 850px)";

const FUNNEL_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">' +
  '<path d="M204,136a12,12,0,0,1-12,12H64a12,12,0,0,1,0-24H192A12,12,0,0,1,204,136Zm28-60H24a12,12,0,0,0,0,24H232a12,12,0,0,0,0-24Zm-80,96H104a12,12,0,0,0,0,24h48a12,12,0,0,0,0-24Z"/>' +
  "</svg>";

const CLOSE_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">' +
  '<path d="M208.5,191.5a12,12,0,0,1-17,17L128,145l-63.5,63.5a12,12,0,0,1-17-17L111,128,47.5,64.5a12,12,0,0,1,17-17L128,111l63.5-63.5a12,12,0,0,1,17,17L145,128Z"/>' +
  "</svg>";

function setupMobileFilterPopover(bar, section, deps) {
  const { state } = deps || {};

  const mql = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
  let active = null; // null when torn down, object with refs when set up

  function build() {
    if (active) return;

    // Wrapper holding the trigger + filter-bar search input side-by-side.
    const stickyBar = document.createElement("div");
    stickyBar.className = "mobile-filter-bar";

    const triggerRow = document.createElement("div");
    triggerRow.className = "mobile-filter-bar-row";

    // The existing filter-bar popover + its More toggle do the heavy
    // lifting on desktop; on mobile the Filter trigger just proxies a
    // click to that toggle so the same popover (now including featured
    // terms) opens with a single source of truth.
    const moreToggle = bar.querySelector(".filter-bar-toggle");
    if (!moreToggle) return;

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "btn btn-sm mobile-filter-bar-trigger";
    trigger.setAttribute("aria-expanded", "false");
    trigger.setAttribute("aria-controls", moreToggle.getAttribute("aria-controls") || "");
    trigger.innerHTML = FUNNEL_ICON_SVG + " Filter";

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      // Stop here so the document-level outside-click handler in popover.js
      // doesn't see this click — it would treat the mobile trigger as an
      // "outside" click and close the popover the same tick we opened it.
      e.stopPropagation();
      const filterPopover = bar.querySelector(".popover");
      const inst = filterPopover?.__popoverInstance;
      if (!inst) return;
      if (filterPopover.hidden) inst.open();
      else inst.close(true);
    });

    // Pull the search wrapper out of the filter-bar so it sits next to the
    // Filter trigger on mobile. We only relocate the wrapper (not the
    // input itself) so the filter-bar's internal JS listeners stay bound.
    const searchEl = bar.querySelector(".filter-bar-search");
    let searchParent = null;
    let searchNext = null;
    if (searchEl) {
      searchParent = searchEl.parentNode;
      searchNext = searchEl.nextSibling;
    }

    stickyBar.appendChild(triggerRow);
    triggerRow.appendChild(trigger);
    if (searchEl) triggerRow.appendChild(searchEl);

    // Selected-filter chip row, populated in refresh(). Clicks are
    // delegated here — each chip proxies to its matching filter-bar-btn
    // so the existing toggle logic runs.
    const chipRow = document.createElement("div");
    chipRow.className = "mobile-filter-bar-chips";
    chipRow.hidden = true;
    chipRow.addEventListener("click", (e) => {
      const chip = e.target.closest(".mobile-filter-bar-chip");
      if (!chip) return;
      const term = chip.dataset.term;
      const taxonomy = chip.dataset.taxonomy;
      if (!term) return;
      const selector = taxonomy
        ? `.filter-bar-btn[data-term="${term}"][data-taxonomy="${taxonomy}"]`
        : `.filter-bar-btn[data-term="${term}"]`;
      const match = bar.querySelector(selector);
      if (match) match.click();
    });
    stickyBar.appendChild(chipRow);

    // Drop the sticky bar right before the inline filter-bar so the two
    // occupy the same slot in the document.
    bar.parentNode.insertBefore(stickyBar, bar);

    active = {
      stickyBar,
      trigger,
      chipRow,
      searchEl,
      searchParent,
      searchNext,
    };

    refresh();
  }

  function refresh() {
    if (!active) return;

    // Rebuild the chip row from whichever .filter-bar-btn pills are
    // currently active. Works for both client mode (state-driven) and
    // ALM mode (mutation-observer-driven) because both keep the pills'
    // .active class in sync with the filter state.
    const activeBtns = bar.querySelectorAll(
      ".filter-bar-btn.active[data-term]",
    );

    active.chipRow.replaceChildren();
    activeBtns.forEach((btn) => {
      // Clone the active pill verbatim so the chip mirrors the desktop
      // pill exactly — including language buttons whose visible label is
      // swapped via inner spans (just the language name when pressed).
      const chip = btn.cloneNode(true);
      chip.removeAttribute("id");
      chip.classList.add("mobile-filter-bar-chip");
      active.chipRow.appendChild(chip);
    });

    active.chipRow.hidden = activeBtns.length === 0;
  }

  function teardown() {
    if (!active) return;

    // Restore the search wrapper to its original slot inside the filter-bar.
    if (active.searchEl && active.searchParent) {
      if (
        active.searchNext &&
        active.searchNext.parentNode === active.searchParent
      ) {
        active.searchParent.insertBefore(active.searchEl, active.searchNext);
      } else {
        active.searchParent.appendChild(active.searchEl);
      }
    }

    active.stickyBar.remove();
    active = null;
  }

  function reflect(e) {
    if (e.matches) build();
    else teardown();
  }

  mql.addEventListener("change", reflect);
  reflect(mql);

  return { refresh };
}

// ---------------------------------------------------------------------------
// Popover positioning (shared)
// ---------------------------------------------------------------------------

function positionPopover(popover, toggle) {
  const bar = popover.closest(".filter-bar");
  const barRect = bar.getBoundingClientRect();
  const toggleRect = toggle.getBoundingClientRect();

  // Round down so we never use subpixel values here. Browsers end up
  // rendering those at half-pixel positions which reads as a blurry /
  // slightly misaligned popover edge against the bar below it.
  const toggleTop = Math.floor(toggleRect.top - barRect.top);
  const toggleRight = Math.floor(barRect.right - toggleRect.right);

  popover.style.left = "";
  popover.style.right = `${toggleRight}px`;
  popover.style.top = `${toggleTop}px`;

  const popoverRect = popover.getBoundingClientRect();

  if (popoverRect.left < 16) {
    const toggleLeft = Math.floor(toggleRect.left - barRect.left);
    popover.style.right = "";
    popover.style.left = `${toggleLeft}px`;
  }
}

// Initialize on DOM ready.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initFilterBars);
} else {
  initFilterBars();
}
